/**
 * metaLeadGen/components/LeadHistoryCohortSection.tsx
 *
 * Buying Cycle analytics section.
 * Receives pre-computed LeadHistoryAnalytics from the parent.
 *
 * Layout:
 *   Row 1: Lead Cohort Revenue Heatmap (full width)
 *   Row 2: Avg Days to Close (full width)
 *   Row 3: Pipeline Aging & Bottleneck Analysis (full width, enhanced)
 *   Row 4: Time-to-Stage Funnel (full width)
 *   Row 5: Cohort Sales Cycle Curve (full width, enhanced)
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  BarChart, Bar, ComposedChart, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, ReferenceLine, Cell,
} from "recharts";
import { branding } from "../../../../config/branding";

function withAlpha(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
import { getLeadHistoryAnalytics } from "../../../../crmFunnel/leadHistory/service";
import type {
  LeadHistoryAnalytics,
  TimeToCloseBucket,
  AudienceBreakdownRow,
  CohortCurveLine,
  CohortCurvePoint,
  AgingBucketRow,
} from "../../../../crmFunnel/leadHistory/types";
import { TIME_TO_CLOSE_BUCKETS, AGING_BUCKETS } from "../../../../crmFunnel/leadHistory/types";

// ─────────────────────────────────────────────
// FORMATTERS
// ─────────────────────────────────────────────
const fmtCur = (v: number) =>
  v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M`
  : v >= 1_000   ? `$${(v / 1_000).toFixed(0)}k`
  : `$${v.toFixed(0)}`;
const fmtPct  = (v: number) => `${v.toFixed(1)}%`;
const fmtDays = (v: number) => `${v.toFixed(1)}d`;
const fmtNum  = (v: number) => String(Math.round(v));

// ─────────────────────────────────────────────
// SECTION DIVIDER
// ─────────────────────────────────────────────
function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-border" />
      <div className="flex items-center gap-2 px-3">
        <div className="w-5 h-5 rounded-md bg-[var(--brand-primary)]/15 flex items-center justify-center">
          <div className="w-2 h-2 rounded-full bg-[var(--brand-primary)]" />
        </div>
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest whitespace-nowrap">
          {label}
        </span>
      </div>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

// ─────────────────────────────────────────────
// CARD WRAPPER
// ─────────────────────────────────────────────
function AnalyticsCard({
  title, description, children, controls, className = "",
}: {
  title: string; description?: string; children: React.ReactNode;
  controls?: React.ReactNode; className?: string;
}) {
  return (
    <div className={`rounded-xl border border-border bg-card p-5 space-y-4 ${className}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-foreground">{title}</p>
          {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
        </div>
        {controls && <div className="flex items-center gap-2 flex-wrap">{controls}</div>}
      </div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────
// SMALL SELECT
// ─────────────────────────────────────────────
function MiniSelect<T extends string>({
  value, options, onChange,
}: {
  value: T; options: Array<{ value: T; label: string }>; onChange: (v: T) => void;
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value as T)}
      className="text-xs rounded-lg border border-border bg-background px-2.5 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand-ring)]"
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ─────────────────────────────────────────────
// HEATMAP CELL COLOR
// ─────────────────────────────────────────────
function heatColor(value: number, max: number): string {
  if (max === 0 || value === 0) return "transparent";
  const intensity = Math.min(value / max, 1);
  const alpha = 0.08 + intensity * 0.55;
  return `rgba(97, 93, 255, ${alpha})`;
}

// ─────────────────────────────────────────────
// 1. COHORT REVENUE HEATMAP
// ─────────────────────────────────────────────
type HeatmapMetric = "revenue" | "closedWonDeals" | "leadToCloseRate" | "applications" | "applicationRate";

const ROW_HEIGHT_PX = 33; // approximate px per row
const MAX_VISIBLE_ROWS = 12;

function CohortHeatmap({ data }: { data: ReturnType<typeof getLeadHistoryAnalytics>["cohortHeatmap"] }) {
  const [metric, setMetric] = useState<HeatmapMetric>("closedWonDeals");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom so newest weeks are visible
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [data]);

  const metricOptions: Array<{ value: HeatmapMetric; label: string }> = [
    { value: "closedWonDeals",  label: "Closed Deals" },
    { value: "applications",    label: "Applications" },
    { value: "revenue",         label: "Revenue" },
    { value: "leadToCloseRate", label: "Close Rate %" },
    { value: "applicationRate", label: "App Rate %" },
  ];

  const maxPerBucket: Record<TimeToCloseBucket, number> = {} as Record<TimeToCloseBucket, number>;
  for (const bucket of TIME_TO_CLOSE_BUCKETS) {
    maxPerBucket[bucket] = Math.max(...data.map(row => (row.cells[bucket] as Record<string, number>)[metric] ?? 0), 0.001);
  }

  const formatCell = (v: number) => {
    if (metric === "revenue")                                   return v === 0 ? "—" : fmtCur(v);
    if (metric === "closedWonDeals" || metric === "applications") return v === 0 ? "—" : String(v);
    return v === 0 ? "—" : fmtPct(v);
  };

  const totals = useMemo(() => {
    const totalLeads   = data.reduce((sum, row) => sum + row.totalLeads, 0);
    const totalRevenue = data.reduce((sum, row) => sum + row.totalRevenue, 0);
    const totalClosedWon = data.reduce((sum, row) => sum + row.totalClosedWon, 0);
    const totalApps    = data.reduce((sum, row) =>
      sum + TIME_TO_CLOSE_BUCKETS.reduce((s, b) => s + (row.cells[b].applications ?? 0), 0), 0);
    const bucketTotals = {} as Record<TimeToCloseBucket, number>;
    const bucketApps   = {} as Record<TimeToCloseBucket, number>;
    const bucketDeals  = {} as Record<TimeToCloseBucket, number>;
    for (const bucket of TIME_TO_CLOSE_BUCKETS) {
      bucketTotals[bucket] = data.reduce((sum, row) => sum + ((row.cells[bucket] as Record<string, number>)[metric] ?? 0), 0);
      bucketApps[bucket]   = data.reduce((sum, row) => sum + (row.cells[bucket].applications ?? 0), 0);
      bucketDeals[bucket]  = data.reduce((sum, row) => sum + row.cells[bucket].closedWonDeals, 0);
    }
    const metricSum = Object.values(bucketTotals).reduce((s, v) => s + v, 0);
    return { totalLeads, totalRevenue, totalClosedWon, totalApps, bucketTotals, bucketApps, bucketDeals, metricSum };
  }, [data, metric]);

  // % of all-buckets total for the active metric (count-based metrics only)
  const showPctRow = metric === "closedWonDeals" || metric === "applications";

  if (data.length === 0) {
    return (
      <AnalyticsCard title="Optin Cohort Revenue Heatmap" description="Weekly cohorts vs. time-to-close buckets">
        <p className="text-sm text-muted-foreground text-center py-8">No cohort data for the selected period.</p>
      </AnalyticsCard>
    );
  }

  const maxScrollHeight = ROW_HEIGHT_PX * MAX_VISIBLE_ROWS;

  const metricLabel = metric === "applications" ? "Applications" : metric === "closedWonDeals" ? "Closed Deals" : "";

  return (
    <AnalyticsCard
      title="Optin Cohort Revenue Heatmap"
      description={
        metric === "applications"
          ? "Cells show when optins booked their first call (application) relative to their optin date."
          : "Cells show when optins closed, relative to their optin date. Each cell = deals or chosen metric."
      }
      controls={<MiniSelect value={metric} options={metricOptions} onChange={setMetric} />}
    >
      {/* Summary badges */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] px-2.5 py-1 rounded-full font-medium">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary)]" />
          {totals.totalApps} total applications
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs bg-emerald-500/10 text-emerald-400 px-2.5 py-1 rounded-full font-medium">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          {totals.totalClosedWon} total closed deals
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs table-fixed">
          <thead>
            <tr>
              <th className="text-left text-muted-foreground font-medium py-2 pr-4 whitespace-nowrap min-w-[120px]">Optin Week</th>
              <th className="text-center text-muted-foreground font-medium py-2 px-2 min-w-[52px]">Optins</th>
              {TIME_TO_CLOSE_BUCKETS.map(b => (
                <th key={b} className="text-center text-muted-foreground font-medium py-2 px-2 min-w-[72px] whitespace-nowrap">{b}</th>
              ))}
              <th className="text-right text-muted-foreground font-medium py-2 pl-4 min-w-[80px]">Total Rev.</th>
            </tr>
          </thead>
        </table>
        {/* Scrollable rows — max 12 visible */}
        <div
          ref={scrollRef}
          style={{ maxHeight: `${maxScrollHeight}px` }}
          className="overflow-y-auto"
        >
          <table className="w-full text-xs table-fixed">
            <tbody>
              {data.map(row => (
                <tr key={row.cohortWeek} className="border-t border-border/50 hover:bg-muted/20 transition-colors">
                  <td className="py-2 pr-4 font-medium text-foreground whitespace-nowrap min-w-[120px]">{row.cohortLabel}</td>
                  <td className="py-2 px-2 text-center text-muted-foreground min-w-[52px]">{row.totalLeads}</td>
                  {TIME_TO_CLOSE_BUCKETS.map(b => {
                    const val = ((row.cells[b] as Record<string, number>)[metric] ?? 0);
                    return (
                      <td key={b} className="py-2 px-2 text-center font-medium rounded transition-colors min-w-[72px]"
                        style={{ backgroundColor: heatColor(val, maxPerBucket[b]) }}>
                        <span className={val === 0 ? "text-muted-foreground/40" : "text-foreground"}>{formatCell(val)}</span>
                      </td>
                    );
                  })}
                  <td className="py-2 pl-4 text-right font-semibold text-[var(--brand-primary)] min-w-[80px]">
                    {row.totalRevenue > 0 ? fmtCur(row.totalRevenue) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Totals + % rows */}
        <table className="w-full text-xs table-fixed border-t-2 border-border">
          <tbody>
            {/* Total metric row */}
            <tr className="bg-muted/30">
              <td className="py-2 pr-4 font-semibold text-foreground whitespace-nowrap min-w-[120px]">Total</td>
              <td className="py-2 px-2 text-center font-semibold text-foreground min-w-[52px]">{totals.totalLeads}</td>
              {TIME_TO_CLOSE_BUCKETS.map(b => (
                <td key={b} className="py-2 px-2 text-center font-semibold text-foreground min-w-[72px]">
                  {formatCell(totals.bucketTotals[b])}
                </td>
              ))}
              <td className="py-2 pl-4 text-right font-semibold text-[var(--brand-primary)] min-w-[80px]">
                {totals.totalRevenue > 0 ? fmtCur(totals.totalRevenue) : "—"}
              </td>
            </tr>
            {/* % of total row (only for count-based metrics) */}
            {showPctRow && totals.metricSum > 0 && (
              <tr className="bg-muted/10">
                <td className="py-1.5 pr-4 text-muted-foreground whitespace-nowrap min-w-[120px] italic">
                  % of {metricLabel}
                </td>
                <td className="min-w-[52px]" />
                {TIME_TO_CLOSE_BUCKETS.map(b => {
                  const pct = (totals.bucketTotals[b] / totals.metricSum) * 100;
                  return (
                    <td key={b} className="py-1.5 px-2 text-center text-muted-foreground min-w-[72px]">
                      {totals.bucketTotals[b] > 0 ? fmtPct(pct) : "—"}
                    </td>
                  );
                })}
                <td className="min-w-[80px]" />
              </tr>
            )}
            {/* Both-totals summary row */}
            <tr className="border-t border-border/50 bg-muted/5">
              <td className="py-1.5 pr-4 text-muted-foreground whitespace-nowrap min-w-[120px] text-[10px] uppercase tracking-wider">
                Apps / Deals
              </td>
              <td className="min-w-[52px]" />
              {TIME_TO_CLOSE_BUCKETS.map(b => (
                <td key={b} className="py-1.5 px-2 text-center min-w-[72px]">
                  <span className="text-[var(--brand-primary)] font-medium">{totals.bucketApps[b] || "—"}</span>
                  <span className="text-muted-foreground/60 mx-0.5">/</span>
                  <span className="text-emerald-400 font-medium">{totals.bucketDeals[b] || "—"}</span>
                </td>
              ))}
              <td className="min-w-[80px]" />
            </tr>
          </tbody>
        </table>
      </div>
    </AnalyticsCard>
  );
}

// ─────────────────────────────────────────────
// 2. AUDIENCE EFFICIENCY ANALYSIS
// ─────────────────────────────────────────────

type AudienceLineMetric = "avgDaysToClose" | "closeRate" | "revenuePerLead" | "cashCollected";
type AudienceSortBy    = "leads" | "closedWonDeals" | "avgDaysToClose" | "revenue" | "closeRate";

interface AudienceChartRow {
  label:                string;
  fullLabel:            string;
  closedWonDeals:       number;
  avgDaysToClose:       number;
  closeRate:            number;
  revenuePerLead:       number;
  cashCollected:        number;
  leads:                number;
  lineValue:            number;
  colorIdx:             number;
  applications:         number;
  applicationRate:      number;
  avgDaysToApplication: number;
}

const AUD_LINE_METRIC_OPTIONS: Array<{ value: AudienceLineMetric; label: string }> = [
  { value: "avgDaysToClose", label: "Line: Avg Days to Close" },
  { value: "closeRate",      label: "Line: Close Rate (%)"    },
  { value: "revenuePerLead", label: "Line: Rev / Optin"        },
  { value: "cashCollected",  label: "Line: Cash Collected"    },
];

const AUD_SORT_OPTIONS: Array<{ value: AudienceSortBy; label: string }> = [
  { value: "leads",          label: "Sort: Optins ↓"      },
  { value: "closedWonDeals", label: "Sort: Deals ↓"      },
  { value: "avgDaysToClose", label: "Sort: Speed ↑"      },
  { value: "revenue",        label: "Sort: Revenue ↓"    },
  { value: "closeRate",      label: "Sort: Close Rate ↓" },
];

interface AudienceTooltipProps {
  active?:     boolean;
  payload?:    Array<{ name: string; value: number; dataKey: string }>;
  label?:      string;
  rows:        AudienceChartRow[];
  lineMetric:  AudienceLineMetric;
  acctAvgDays: number;
}

function AudienceTooltip({ active, payload, label, rows, lineMetric, acctAvgDays }: AudienceTooltipProps) {
  if (!active || !payload?.length || label == null) return null;
  const row = rows.find(r => r.label === label || r.fullLabel === label);
  if (!row) return null;
  return (
    <div className="bg-card border border-border rounded-xl p-3 text-xs shadow-xl max-w-[240px] space-y-2 z-50">
      <p className="font-semibold text-foreground text-[11px] leading-4 pb-1.5 border-b border-border">{row.fullLabel}</p>
      <div className="grid grid-cols-[1fr_auto] gap-x-4 text-muted-foreground leading-5">
        <span>Optins</span>            <span className="text-right font-semibold text-foreground">{row.leads}</span>
        <span>Applications</span>      <span className="text-right font-medium text-[var(--brand-primary)]">{row.applications} ({fmtPct(row.applicationRate)})</span>
        <span>Avg Days to App</span>   <span className="text-right font-medium text-foreground">{row.avgDaysToApplication > 0 ? fmtDays(row.avgDaysToApplication) : "—"}</span>
        <span>Closed Won</span>        <span className="text-right font-medium text-foreground">{row.closedWonDeals}</span>
        <span>Close Rate</span>        <span className="text-right font-medium text-foreground">{fmtPct(row.closeRate)}</span>
        <span>Avg Days to Close</span> <span className="text-right font-medium text-foreground">{row.avgDaysToClose > 0 ? fmtDays(row.avgDaysToClose) : "—"}</span>
        <span>Cash Collected</span>    <span className="text-right font-medium text-foreground">{row.cashCollected > 0 ? fmtCur(row.cashCollected) : "—"}</span>
      </div>
      {acctAvgDays > 0 && row.avgDaysToClose > 0 && (
        <p className="text-[10px] text-muted-foreground pt-1 border-t border-border">
          {row.avgDaysToClose < acctAvgDays
            ? `⚡ Closes ${(acctAvgDays - row.avgDaysToClose).toFixed(1)}d faster than avg`
            : `⏱ Closes ${(row.avgDaysToClose - acctAvgDays).toFixed(1)}d slower than avg`}
        </p>
      )}
    </div>
  );
}

function AudienceEfficiencyAnalysis({
  initialData,
}: {
  initialData: AudienceBreakdownRow[];
}) {
  const [lineMetric, setLineMetric] = useState<AudienceLineMetric>("avgDaysToClose");
  const [sortBy, setSortBy]         = useState<AudienceSortBy>("leads");

  const { chartData, acctAvgDays, acctAvgForMetric } = useMemo(() => {
    const withDeals   = initialData.filter(r => r.closedWonDeals > 0);
    const acctAvgDays = withDeals.length > 0
      ? withDeals.reduce((s, r) => s + r.averageDaysToClose, 0) / withDeals.length
      : 0;
    const acctAvgForMetric: Record<AudienceLineMetric, number> = {
      avgDaysToClose: acctAvgDays,
      closeRate:      initialData.length > 0 ? initialData.reduce((s, r) => s + r.leadToCloseRate, 0) / initialData.length : 0,
      revenuePerLead: initialData.length > 0 ? initialData.reduce((s, r) => s + r.revenuePerLead, 0) / initialData.length : 0,
      cashCollected:  initialData.length > 0 ? initialData.reduce((s, r) => s + r.revenue, 0) / initialData.length : 0,
    };

    const getLineVal = (r: AudienceBreakdownRow): number => {
      switch (lineMetric) {
        case "avgDaysToClose": return parseFloat(r.averageDaysToClose.toFixed(1));
        case "closeRate":      return parseFloat(r.leadToCloseRate.toFixed(1));
        case "revenuePerLead": return parseFloat(r.revenuePerLead.toFixed(0));
        case "cashCollected":  return r.revenue;
      }
    };

    const rows: AudienceChartRow[] = initialData.map((r, i) => ({
      label:                r.label.length > 24 ? r.label.slice(0, 23) + "…" : r.label,
      fullLabel:            r.label,
      closedWonDeals:       r.closedWonDeals,
      avgDaysToClose:       parseFloat(r.averageDaysToClose.toFixed(1)),
      closeRate:            parseFloat(r.leadToCloseRate.toFixed(1)),
      revenuePerLead:       parseFloat(r.revenuePerLead.toFixed(0)),
      cashCollected:        r.revenue,
      leads:                r.leads,
      lineValue:            getLineVal(r),
      colorIdx:             i,
      applications:         r.applications ?? 0,
      applicationRate:      parseFloat((r.applicationRate ?? 0).toFixed(1)),
      avgDaysToApplication: parseFloat((r.averageDaysToApplication ?? 0).toFixed(1)),
    }));

    switch (sortBy) {
      case "leads":          rows.sort((a, b) => b.leads - a.leads); break;
      case "closedWonDeals": rows.sort((a, b) => b.closedWonDeals - a.closedWonDeals); break;
      case "avgDaysToClose": rows.sort((a, b) => a.avgDaysToClose - b.avgDaysToClose); break;
      case "revenue":        rows.sort((a, b) => b.cashCollected - a.cashCollected); break;
      case "closeRate":      rows.sort((a, b) => b.closeRate - a.closeRate); break;
    }

    return { chartData: rows.slice(0, 20), acctAvgDays, acctAvgForMetric };
  }, [initialData, sortBy, lineMetric]);

  const isHorizontal   = chartData.length > 8;
  const hasAngleLabels = chartData.length > 5;
  const lineColor      = branding.colors.chart[0];
  const benchmarkColor = branding.colors.chart[3];

  const acctAvg    = acctAvgForMetric[lineMetric];
  const acctAvgFmt = (() => {
    switch (lineMetric) {
      case "avgDaysToClose": return fmtDays(acctAvg);
      case "closeRate":      return fmtPct(acctAvg);
      case "revenuePerLead": return fmtCur(acctAvg);
      case "cashCollected":  return fmtCur(acctAvg);
    }
  })();
  const lineAxisLabel = {
    avgDaysToClose: "Avg Days to Close",
    closeRate:      "Close Rate (%)",
    revenuePerLead: "Rev / Optin ($)",
    cashCollected:  "Cash Collected ($)",
  }[lineMetric];

  const avgLeadsAcrossRows = chartData.length > 0
    ? chartData.reduce((s, r) => s + r.leads, 0) / chartData.length
    : 0;

  const controls = (
    <div className="flex items-center gap-2 flex-wrap">
      <MiniSelect value={lineMetric} options={AUD_LINE_METRIC_OPTIONS} onChange={v => setLineMetric(v as AudienceLineMetric)} />
      <MiniSelect value={sortBy}     options={AUD_SORT_OPTIONS}        onChange={v => setSortBy(v as AudienceSortBy)} />
    </div>
  );

  if (chartData.length === 0) {
    return (
      <AnalyticsCard
        title="Audience Efficiency Analysis"
        description="Optins, closed deals & closing speed by traffic source"
        controls={controls}
      >
        <p className="text-sm text-muted-foreground text-center py-8">No source data for the selected period.</p>
      </AnalyticsCard>
    );
  }

  return (
    <AnalyticsCard
      title="Audience Efficiency Analysis"
      description={isHorizontal
        ? "Optins by traffic source · sorted by volume · hover for details"
        : "Bars = Optins (left axis) · Line = selected metric (right axis) · sorted by volume"}
      controls={controls}
    >
      {isHorizontal ? (
        // ── Horizontal layout (>8 sources) ─────────────────────────────
        <div style={{ height: Math.max(320, chartData.length * 42 + 48) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={chartData} margin={{ top: 8, right: 72, bottom: 28, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} tickFormatter={fmtNum}
                label={{ value: sortBy === "leads" ? "Optins" : "Closed Won Deals", position: "insideBottom", offset: -8, fontSize: 10, fill: "var(--muted-foreground)" }} />
              <YAxis type="category" dataKey="label" width={160}
                tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
              <Tooltip content={<AudienceTooltip rows={chartData} lineMetric={lineMetric} acctAvgDays={acctAvgDays} />} />
              <Bar dataKey={sortBy === "closedWonDeals" ? "closedWonDeals" : "leads"} name={sortBy === "closedWonDeals" ? "Closed Won" : "Optins"} maxBarSize={28} radius={[0, 3, 3, 0]}
                fill={withAlpha(branding.colors.primary, 0.70)} />
              {avgLeadsAcrossRows > 0 && (
                <ReferenceLine x={avgLeadsAcrossRows} stroke={benchmarkColor} strokeDasharray="4 2" strokeOpacity={0.6}
                  label={{ value: `Avg ${Math.round(avgLeadsAcrossRows)}`, position: "insideTopRight", fontSize: 9, fill: benchmarkColor }} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        // ── Vertical layout (≤8 sources): bars + secondary line ────────
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 72, bottom: hasAngleLabels ? 80 : 40, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
              angle={hasAngleLabels ? -35 : 0}
              textAnchor={hasAngleLabels ? "end" : "middle"}
              interval={0}
              height={hasAngleLabels ? 72 : 36}
            />
            <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} tickFormatter={fmtNum} width={44}
              label={{ value: "Optins", angle: -90, position: "insideLeft", offset: 12, fontSize: 9, fill: "var(--muted-foreground)" }} />
            <YAxis yAxisId="right" orientation="right" width={56}
              tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
              tickFormatter={v => {
                switch (lineMetric) {
                  case "avgDaysToClose": return fmtDays(v);
                  case "closeRate":      return fmtPct(v);
                  default:               return fmtCur(v);
                }
              }}
              label={{ value: lineAxisLabel, angle: 90, position: "insideRight", offset: 12, fontSize: 9, fill: "var(--muted-foreground)" }} />
            <Tooltip content={<AudienceTooltip rows={chartData} lineMetric={lineMetric} acctAvgDays={acctAvgDays} />} />
            <Bar yAxisId="left" dataKey="leads" name="Optins" radius={[4, 4, 0, 0]} maxBarSize={60}
              fill={withAlpha(branding.colors.primary, 0.70)} />
            <Line yAxisId="right" type="linear" dataKey="lineValue" name={lineAxisLabel}
              stroke={lineColor} strokeWidth={1.5} strokeOpacity={0.9}
              dot={{ r: 3.5, fill: lineColor, strokeWidth: 0 }} activeDot={{ r: 5 }} />
            {acctAvg > 0 && (
              <ReferenceLine yAxisId="right" y={acctAvg} stroke={benchmarkColor} strokeDasharray="4 2" strokeOpacity={0.7}
                label={{ value: `Avg = ${acctAvgFmt}`, position: "insideTopRight", fontSize: 9, fill: benchmarkColor }} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      )}

      {/* ── Second chart: Avg Days Optin → Application ── */}
      {chartData.some(r => r.applications > 0) && (
        <div className="pt-4 border-t border-border space-y-2">
          <p className="text-xs font-semibold text-foreground">
            Avg Days: Optin → First Call
          </p>
          <p className="text-[11px] text-muted-foreground">
            How long it takes each source's optins to book a call (application)
          </p>
          {(() => {
            const appData = [...chartData]
              .filter(r => r.applications > 0)
              .sort((a, b) => a.avgDaysToApplication - b.avgDaysToApplication);
            const maxDays = Math.max(...appData.map(r => r.avgDaysToApplication), 1);
            const avgDaysToApp = appData.reduce((s, r) => s + r.avgDaysToApplication, 0) / appData.length;
            return (
              <div style={{ height: Math.max(220, appData.length * 42 + 48) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart layout="vertical" data={appData} margin={{ top: 4, right: 80, bottom: 12, left: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                    <XAxis type="number" domain={[0, Math.ceil(maxDays * 1.15)]}
                      tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                      tickFormatter={v => `${v}d`}
                      label={{ value: "Avg Days to Application", position: "insideBottom", offset: -4, fontSize: 10, fill: "var(--muted-foreground)" }} />
                    <YAxis type="category" dataKey="label" width={160}
                      tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const r = appData.find(x => x.label === label || x.fullLabel === label);
                        if (!r) return null;
                        return (
                          <div className="bg-card border border-border rounded-xl p-3 text-xs shadow-xl space-y-1.5 z-50">
                            <p className="font-semibold text-foreground border-b border-border pb-1.5">{r.fullLabel}</p>
                            <div className="grid grid-cols-[1fr_auto] gap-x-4 text-muted-foreground leading-5">
                              <span>Optins</span>          <span className="text-right font-medium text-foreground">{r.leads}</span>
                              <span>Applications</span>    <span className="text-right font-medium text-[var(--brand-primary)]">{r.applications} ({fmtPct(r.applicationRate)})</span>
                              <span>Avg Days to App</span> <span className="text-right font-semibold text-foreground">{fmtDays(r.avgDaysToApplication)}</span>
                            </div>
                            {avgDaysToApp > 0 && (
                              <p className="text-[10px] text-muted-foreground border-t border-border pt-1">
                                {r.avgDaysToApplication < avgDaysToApp
                                  ? `⚡ Books ${(avgDaysToApp - r.avgDaysToApplication).toFixed(1)}d faster than avg`
                                  : `⏱ Books ${(r.avgDaysToApplication - avgDaysToApp).toFixed(1)}d slower than avg`}
                              </p>
                            )}
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="avgDaysToApplication" name="Avg Days to Application" maxBarSize={26} radius={[0, 3, 3, 0]}>
                      {appData.map((row, i) => (
                        <Cell key={row.fullLabel} fill={branding.colors.chart[i % branding.colors.chart.length]} />
                      ))}
                    </Bar>
                    {avgDaysToApp > 0 && (
                      <ReferenceLine x={avgDaysToApp} stroke={branding.colors.chart[3]} strokeDasharray="4 2" strokeOpacity={0.7}
                        label={{ value: `Avg ${avgDaysToApp.toFixed(1)}d`, position: "insideTopRight", fontSize: 9, fill: branding.colors.chart[3] }} />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            );
          })()}
        </div>
      )}

      {/* Summary table */}
      <div className="pt-3 border-t border-border overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="text-left text-muted-foreground font-medium py-2 pr-4 whitespace-nowrap">Source</th>
              <th className="text-right text-muted-foreground font-medium py-2 px-2">Optins</th>
              <th className="text-right text-muted-foreground font-medium py-2 px-2 whitespace-nowrap">Apps</th>
              <th className="text-right text-muted-foreground font-medium py-2 px-2 whitespace-nowrap">App %</th>
              <th className="text-right text-muted-foreground font-medium py-2 px-2 whitespace-nowrap">Days→App</th>
              <th className="text-right text-muted-foreground font-medium py-2 px-2 whitespace-nowrap">Closed</th>
              <th className="text-right text-muted-foreground font-medium py-2 px-2 whitespace-nowrap">Close %</th>
              <th className="text-right text-muted-foreground font-medium py-2 pl-2 whitespace-nowrap">Cash</th>
            </tr>
          </thead>
          <tbody>
            {chartData.map((row, i) => (
              <tr key={row.fullLabel} className="border-t border-border/50 hover:bg-muted/10 transition-colors">
                <td className="py-2 pr-4">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: branding.colors.chart[i % branding.colors.chart.length] }} />
                    <span className="font-medium text-foreground truncate max-w-[150px]" title={row.fullLabel}>{row.fullLabel}</span>
                  </div>
                </td>
                <td className="py-2 px-2 text-right font-semibold text-foreground">{row.leads}</td>
                <td className="py-2 px-2 text-right text-[var(--brand-primary)] font-medium">{row.applications || "—"}</td>
                <td className="py-2 px-2 text-right text-foreground">{row.applications > 0 ? fmtPct(row.applicationRate) : "—"}</td>
                <td className="py-2 px-2 text-right text-foreground">{row.avgDaysToApplication > 0 ? fmtDays(row.avgDaysToApplication) : "—"}</td>
                <td className="py-2 px-2 text-right text-foreground">{row.closedWonDeals}</td>
                <td className="py-2 px-2 text-right text-foreground">{fmtPct(row.closeRate)}</td>
                <td className="py-2 pl-2 text-right font-semibold text-[var(--brand-primary)]">{row.cashCollected > 0 ? fmtCur(row.cashCollected) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AnalyticsCard>
  );
}

// ─────────────────────────────────────────────
// 3. PIPELINE AGING & BOTTLENECK ANALYSIS
// ─────────────────────────────────────────────

type AgingDangerLevel = "healthy" | "warning" | "concerning" | "critical";
type AgingStage = "New Lead" | "MQL" | "Opportunity" | "Lost";

interface AgingFilterState {
  source:      string;
  paidOrganic: string;
  campaign:    string;
  dateRange:   string;
}

interface HoveredSegment {
  bucket: string;
  stage:  AgingStage;
  count:  number;
  total:  number;
  totalOpen: number;
}

const AGING_STAGES: AgingStage[] = ["New Lead", "MQL", "Opportunity", "Lost"];

const STAGE_CFG: Record<AgingStage, { bar: string; text: string; dot: string; label: string }> = {
  "New Lead":    { bar: "bg-slate-400/70",   text: "text-slate-300",   dot: "bg-slate-400",   label: "New Lead" },
  "MQL":         { bar: "bg-sky-500/80",     text: "text-sky-300",     dot: "bg-sky-500",     label: "MQL" },
  "Opportunity": { bar: "bg-emerald-500/80", text: "text-emerald-300", dot: "bg-emerald-500", label: "Opportunity" },
  "Lost":        { bar: "bg-red-400/60",     text: "text-red-300",     dot: "bg-red-400",     label: "Lost" },
};

const BUCKET_DANGER: Record<string, AgingDangerLevel> = {
  "0–3 days":   "healthy",
  "4–7 days":   "healthy",
  "8–14 days":  "warning",
  "15–30 days": "concerning",
  "30+ days":   "critical",
};

const BUCKET_MIDPOINTS: Record<string, number> = {
  "0–3 days":   1.5,
  "4–7 days":   5.5,
  "8–14 days":  11,
  "15–30 days": 22.5,
  "30+ days":   40,
};

const DANGER_CFG: Record<AgingDangerLevel, {
  rowBg: string; badge: string; badgeCls: string; icon: string; borderCls: string;
}> = {
  healthy:    { rowBg: "",                          badge: "Healthy",    badgeCls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", icon: "✓",  borderCls: "border-l-emerald-500/30" },
  warning:    { rowBg: "bg-amber-400/[0.04]",       badge: "Warning",    badgeCls: "bg-amber-400/10  text-amber-400  border-amber-400/20",    icon: "⚠",  borderCls: "border-l-amber-400/50" },
  concerning: { rowBg: "bg-orange-400/[0.06]",      badge: "Concerning", badgeCls: "bg-orange-400/10 text-orange-400 border-orange-400/20",   icon: "⚠",  borderCls: "border-l-orange-400/50" },
  critical:   { rowBg: "bg-red-500/[0.08]",         badge: "Critical",   badgeCls: "bg-red-500/10    text-red-400    border-red-500/20",      icon: "●",  borderCls: "border-l-red-500/60" },
};

const AGING_SOURCE_OPTIONS = [
  { value: "all",       label: "All Sources" },
  { value: "youtube",   label: "YouTube" },
  { value: "instagram", label: "Instagram" },
  { value: "paid_meta", label: "Paid Meta" },
  { value: "organic",   label: "Organic" },
  { value: "referral",  label: "Referral" },
  { value: "email",     label: "Email" },
];

const AGING_PAID_OPTIONS = [
  { value: "all",     label: "Paid + Organic" },
  { value: "paid",    label: "Paid Only" },
  { value: "organic", label: "Organic Only" },
];

const DATE_RANGE_OPTIONS = [
  { value: "all",  label: "Last 12 months" },
  { value: "30d",  label: "Last 30 days" },
  { value: "60d",  label: "Last 60 days" },
  { value: "90d",  label: "Last 90 days" },
  { value: "180d", label: "Last 6 months" },
];

function dateRangeToParams(range: string): { startDate?: string; endDate?: string } {
  const dmap: Record<string, number> = { "30d": 30, "60d": 60, "90d": 90, "180d": 180 };
  const days = dmap[range];
  if (!days) return {};
  const start = new Date();
  start.setDate(start.getDate() - days);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate:   new Date().toISOString().slice(0, 10),
  };
}

function getStageCount(row: AgingBucketRow, stage: AgingStage): number {
  return row[stage];
}

// KPI card
function AgingKPICard({ label, value, sub, status }: {
  label: string; value: string; sub?: string;
  status: "ok" | "warning" | "critical" | "neutral";
}) {
  const cls = {
    ok:      "text-emerald-400",
    warning: "text-amber-400",
    critical:"text-red-400",
    neutral: "text-[var(--brand-primary)]",
  }[status];
  return (
    <div className="rounded-lg border border-border bg-muted/20 px-3 py-2.5 min-w-0">
      <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider truncate">{label}</p>
      <p className={`text-xl font-bold mt-0.5 ${cls}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground truncate">{sub}</p>}
    </div>
  );
}

function PipelineAgingBottleneck({
  initialData,
  campaigns,
}: {
  initialData: AgingBucketRow[];
  campaigns: string[];
}) {
  const [agingData, setAgingData]       = useState<AgingBucketRow[]>(initialData);
  const [filters, setFilters]           = useState<AgingFilterState>({ source: "all", paidOrganic: "all", campaign: "all", dateRange: "all" });
  const [loading, setLoading]           = useState(false);
  const [hovered, setHovered]           = useState<HoveredSegment | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Sync when parent refreshes (no active filters)
  useEffect(() => {
    const hasF = filters.source !== "all" || filters.paidOrganic !== "all" || filters.campaign !== "all" || filters.dateRange !== "all";
    if (!hasF) setAgingData(initialData);
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when filters change
  useEffect(() => {
    abortRef.current?.abort();
    const hasF = filters.source !== "all" || filters.paidOrganic !== "all" || filters.campaign !== "all" || filters.dateRange !== "all";
    if (!hasF) {
      setAgingData(initialData);
      return;
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.source !== "all")      params.set("source",      filters.source);
    if (filters.paidOrganic !== "all") params.set("paidOrganic", filters.paidOrganic);
    if (filters.campaign !== "all")    params.set("campaign",    filters.campaign);
    const { startDate, endDate } = dateRangeToParams(filters.dateRange);
    if (startDate) params.set("startDate", startDate);
    if (endDate)   params.set("endDate",   endDate);

    fetch(`/api/buying-cycle/analytics?${params}`, { signal: ctrl.signal })
      .then(r => r.json() as Promise<{ analytics?: { agingDistribution: AgingBucketRow[] } }>)
      .then(data => {
        setAgingData(data.analytics?.agingDistribution ?? initialData);
        setLoading(false);
      })
      .catch(err => { if ((err as Error).name !== "AbortError") setLoading(false); });
    return () => ctrl.abort();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (key: keyof AgingFilterState, val: string) =>
    setFilters(f => ({ ...f, [key]: val }));

  const campaignOptions = useMemo(() => [
    { value: "all", label: "All Campaigns" },
    ...campaigns.slice(0, 25).map(c => ({ value: c, label: c.length > 32 ? c.slice(0, 31) + "…" : c })),
  ], [campaigns]);

  // ── KPI computation ────────────────────────
  const totalOpen = agingData.reduce((s, r) => s + r.total, 0);

  const estAvgAge = totalOpen > 0
    ? agingData.reduce((s, r) => s + r.total * (BUCKET_MIDPOINTS[r.bucket] ?? 0), 0) / totalOpen
    : 0;

  const stale14 = agingData
    .filter(r => r.bucket === "15–30 days" || r.bucket === "30+ days")
    .reduce((s, r) => s + r.total, 0);
  const pctStale14 = totalOpen > 0 ? (stale14 / totalOpen) * 100 : 0;

  const totalMql = agingData.reduce((s, r) => s + r["MQL"], 0);
  const mqlStale14 = agingData
    .filter(r => r.bucket === "15–30 days" || r.bucket === "30+ days")
    .reduce((s, r) => s + r["MQL"], 0);
  const pctMqlStale = totalMql > 0 ? (mqlStale14 / totalMql) * 100 : 0;

  const oldestBucket = [...AGING_BUCKETS].reverse().find(b => agingData.find(r => r.bucket === b)?.total ?? 0 > 0);

  const bottleneck = useMemo(() => {
    const counts = AGING_STAGES.map(stage => ({
      stage,
      count: agingData
        .filter(r => r.bucket === "15–30 days" || r.bucket === "30+ days")
        .reduce((s, r) => s + getStageCount(r, stage), 0),
    }));
    return counts.reduce((max, c) => c.count > max.count ? c : max, counts[0]);
  }, [agingData]);

  // ── Hovered segment details ─────────────────
  const hoveredRow = hovered ? agingData.find(r => r.bucket === hovered.bucket) : null;

  const controls = (
    <div className="flex items-center gap-2 flex-wrap">
      <MiniSelect value={filters.dateRange}   options={DATE_RANGE_OPTIONS}   onChange={v => setFilter("dateRange",   v)} />
      <MiniSelect value={filters.paidOrganic} options={AGING_PAID_OPTIONS}   onChange={v => setFilter("paidOrganic", v)} />
      <MiniSelect value={filters.source}      options={AGING_SOURCE_OPTIONS} onChange={v => setFilter("source",      v)} />
      {campaigns.length > 0 && (
        <MiniSelect value={filters.campaign}  options={campaignOptions}      onChange={v => setFilter("campaign",    v)} />
      )}
      {loading && <span className="text-[10px] text-muted-foreground animate-pulse">Loading…</span>}
    </div>
  );

  return (
    <AnalyticsCard
      title="Pipeline Aging & Bottleneck Analysis"
      description="Open leads only · Stacked by stage (New Lead → MQL → Opportunity → Lost) · Danger zones highlight stale leads"
      controls={controls}
    >
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <AgingKPICard
          label="Est. Avg Lead Age"
          value={`${estAvgAge.toFixed(1)}d`}
          sub={`${totalOpen} open leads`}
          status={estAvgAge > 14 ? "critical" : estAvgAge > 7 ? "warning" : "ok"}
        />
        <AgingKPICard
          label="Stale (14+ days)"
          value={`${pctStale14.toFixed(0)}%`}
          sub={`${stale14} leads`}
          status={pctStale14 > 30 ? "critical" : pctStale14 > 15 ? "warning" : "ok"}
        />
        <AgingKPICard
          label="MQL Stale (14+ d)"
          value={`${pctMqlStale.toFixed(0)}%`}
          sub={`${mqlStale14} of ${totalMql} MQL`}
          status={pctMqlStale > 40 ? "critical" : pctMqlStale > 20 ? "warning" : "ok"}
        />
        <AgingKPICard
          label="Oldest Leads"
          value={oldestBucket ?? "—"}
          sub="highest active bucket"
          status={oldestBucket === "30+ days" ? "critical" : oldestBucket === "15–30 days" ? "warning" : "ok"}
        />
        <AgingKPICard
          label="Biggest Bottleneck"
          value={bottleneck.count > 0 ? bottleneck.stage : "—"}
          sub={bottleneck.count > 0 ? `${bottleneck.count} stale leads` : "No stale leads"}
          status={bottleneck.count > 10 ? "critical" : bottleneck.count > 5 ? "warning" : "ok"}
        />
      </div>

      {/* Legend row */}
      <div className="flex items-center gap-4 flex-wrap pt-1 pb-2 border-b border-border">
        {/* Stage colors */}
        <div className="flex items-center gap-3 flex-wrap">
          {AGING_STAGES.map(s => (
            <div key={s} className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded-sm ${STAGE_CFG[s].dot}`} />
              <span className="text-[10px] text-muted-foreground">{s}</span>
            </div>
          ))}
        </div>
        <div className="h-3 w-px bg-border" />
        {/* Danger levels */}
        <div className="flex items-center gap-3 flex-wrap">
          {(["healthy","warning","concerning","critical"] as AgingDangerLevel[]).map(d => (
            <span key={d} className={`text-[10px] px-2 py-0.5 rounded-full border ${DANGER_CFG[d].badgeCls}`}>
              {DANGER_CFG[d].icon} {DANGER_CFG[d].badge}
            </span>
          ))}
        </div>
      </div>

      {/* Stacked Bar Rows */}
      {totalOpen === 0 && !loading ? (
        <p className="text-sm text-muted-foreground text-center py-8">No open leads found for the selected filters.</p>
      ) : (
        <div className="space-y-2">
          {AGING_BUCKETS.map(bucket => {
            const row = agingData.find(r => r.bucket === bucket);
            const danger = BUCKET_DANGER[bucket] ?? "healthy";
            const dcfg   = DANGER_CFG[danger];
            const total  = row?.total ?? 0;

            return (
              <div
                key={bucket}
                className={`rounded-lg border-l-2 ${dcfg.borderCls} ${dcfg.rowBg} px-3 py-2.5 transition-colors`}
              >
                {/* Row header */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-foreground w-20 flex-shrink-0">{bucket}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${dcfg.badgeCls}`}>
                    {dcfg.icon} {dcfg.badge}
                  </span>
                  <span className="ml-auto text-xs font-semibold text-foreground">
                    {total} {total === 1 ? "lead" : "leads"}
                    {totalOpen > 0 && total > 0 && (
                      <span className="text-muted-foreground font-normal ml-1">
                        ({((total / totalOpen) * 100).toFixed(0)}% of open)
                      </span>
                    )}
                  </span>
                </div>

                {/* Stacked bar */}
                {total > 0 ? (
                  <div className="flex h-8 rounded-md overflow-hidden w-full gap-px">
                    {AGING_STAGES.map(stage => {
                      const count = row ? getStageCount(row, stage) : 0;
                      if (count === 0) return null;
                      const pct   = (count / total) * 100;
                      const scfg  = STAGE_CFG[stage];
                      const isHov = hovered?.bucket === bucket && hovered?.stage === stage;
                      return (
                        <div
                          key={stage}
                          className={`${scfg.bar} flex items-center justify-center overflow-hidden cursor-pointer transition-all relative ${isHov ? "brightness-125 ring-1 ring-white/20" : ""}`}
                          style={{ width: `${pct}%`, minWidth: count > 0 ? 2 : 0 }}
                          onMouseEnter={() => setHovered({ bucket, stage, count, total, totalOpen })}
                          onMouseLeave={() => setHovered(null)}
                        >
                          {pct >= 9 && (
                            <span className={`text-[9px] font-bold select-none ${scfg.text}`}>
                              {pct.toFixed(0)}%
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="h-8 rounded-md bg-muted/20 flex items-center px-3">
                    <span className="text-[10px] text-muted-foreground/40">No leads in this bucket</span>
                  </div>
                )}

                {/* Stage sub-counts */}
                {total > 0 && (
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    {AGING_STAGES.map(stage => {
                      const count = row ? getStageCount(row, stage) : 0;
                      if (count === 0) return null;
                      return (
                        <span key={stage} className={`text-[10px] ${STAGE_CFG[stage].text}`}>
                          {STAGE_CFG[stage].label}: {count}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Hover Detail Panel */}
      <div className={`rounded-lg border border-border bg-muted/20 p-3 text-xs transition-all duration-150 ${hovered ? "opacity-100" : "opacity-40"}`}>
        {hovered && hoveredRow ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-sm ${STAGE_CFG[hovered.stage].dot}`} />
              <span className="font-semibold text-foreground">{hovered.stage}</span>
              <span className="text-muted-foreground">in</span>
              <span className={`font-semibold ${DANGER_CFG[BUCKET_DANGER[hovered.bucket] ?? "healthy"].badgeCls.split(" ")[1]}`}>
                {hovered.bucket}
              </span>
              <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full border ${DANGER_CFG[BUCKET_DANGER[hovered.bucket] ?? "healthy"].badgeCls}`}>
                {DANGER_CFG[BUCKET_DANGER[hovered.bucket] ?? "healthy"].badge}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-1 border-t border-border">
              <div>
                <p className="text-muted-foreground text-[10px]">Count</p>
                <p className="font-bold text-foreground text-sm">{hovered.count}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-[10px]">% of bucket</p>
                <p className="font-bold text-foreground text-sm">
                  {hovered.total > 0 ? ((hovered.count / hovered.total) * 100).toFixed(1) : "0"}%
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-[10px]">% of all open</p>
                <p className="font-bold text-foreground text-sm">
                  {hovered.totalOpen > 0 ? ((hovered.count / hovered.totalOpen) * 100).toFixed(1) : "0"}%
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-[10px]">Est. age range</p>
                <p className="font-bold text-foreground text-sm">{hovered.bucket}</p>
              </div>
            </div>
            {(BUCKET_DANGER[hovered.bucket] === "concerning" || BUCKET_DANGER[hovered.bucket] === "critical") && (
              <p className="text-[10px] text-amber-400/80 pt-1 border-t border-border">
                Use source / campaign filters above to drill down and identify where these stale leads are coming from.
              </p>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground/50 text-center text-[10px]">
            Hover a bar segment to see details
          </p>
        )}
      </div>
    </AnalyticsCard>
  );
}

// ─────────────────────────────────────────────
// 4. TIME-TO-STAGE FUNNEL
// ─────────────────────────────────────────────
function TimeToStageFunnelChart({ data }: { data: ReturnType<typeof getLeadHistoryAnalytics>["timeToStageFunnel"] }) {
  const maxVol = Math.max(...data.stages.map(s => s.volume), 1);

  return (
    <AnalyticsCard title="Time-to-Stage Funnel" description="Volume, conversion rate, and average days to progress through each stage">
      <div className="space-y-3">
        {data.stages.map((stage, i) => {
          const widthPct = (stage.volume / maxVol) * 100;
          return (
            <div key={stage.stage} className="space-y-1">
              <div className="flex items-center gap-3">
                <div className="w-24 text-xs font-medium text-foreground text-right flex-shrink-0">{stage.stage}</div>
                <div className="flex-1 h-8 bg-muted/30 rounded-md overflow-hidden relative">
                  <div className="h-full rounded-md transition-all duration-500"
                    style={{ width: `${widthPct}%`, background: "linear-gradient(90deg, var(--brand-gradient-start), var(--brand-gradient-end))", opacity: 0.7 + (1 - i / data.stages.length) * 0.3 }} />
                  <span className="absolute inset-0 flex items-center px-3 text-xs font-semibold text-white">
                    {stage.volume.toLocaleString()}
                  </span>
                </div>
                <div className="w-16 text-right text-xs text-muted-foreground flex-shrink-0">
                  {i === 0 ? "100%" : `${stage.conversionRateFromPrev.toFixed(0)}%`}
                </div>
              </div>
              {i > 0 && (
                <div className="flex items-center gap-3 ml-24 pl-3">
                  <div className="text-muted-foreground/40 text-xs">↓</div>
                  <div className="text-xs text-muted-foreground">
                    avg <span className="font-semibold text-foreground">{stage.averageDaysFromPrev.toFixed(1)}d</span> to reach this stage
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="pt-3 border-t border-border flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-[var(--brand-primary)]" />
        <p className="text-xs text-muted-foreground">
          Average total optin-to-close: <span className="font-semibold text-foreground">{data.totalLeadToCloseAvgDays.toFixed(1)} days</span>
        </p>
      </div>
    </AnalyticsCard>
  );
}

// ─────────────────────────────────────────────
// 5. COHORT SALES CYCLE CURVE
// ─────────────────────────────────────────────

type CurveMetric = "cumulativeCloseRate" | "cumulativeClosedWon" | "cumulativeRevenue" | "avgDaysToClose";

interface CurveCampaignFilter {
  source: string;
  paidOrganic: string;
  campaign: string;
}

interface AugmentedPoint extends CohortCurvePoint {
  avgDaysToClose: number;
}

interface AugmentedCurveLine {
  cohortWeek: string;
  cohortLabel: string;
  totalLeads: number;
  points: AugmentedPoint[];
}

function augmentCurve(cohort: CohortCurveLine): AugmentedCurveLine {
  let sumDays = 0;
  let prevClosed = 0;
  const points: AugmentedPoint[] = cohort.points.map(pt => {
    const newClosed = pt.cumulativeClosedWon - prevClosed;
    sumDays += newClosed * pt.day;
    prevClosed = pt.cumulativeClosedWon;
    return { ...pt, avgDaysToClose: pt.cumulativeClosedWon > 0 ? sumDays / pt.cumulativeClosedWon : 0 };
  });
  return { ...cohort, points };
}

function getRateAtDay(cohort: AugmentedCurveLine, day: number): number {
  const pt = [...cohort.points].reverse().find(p => p.day <= day);
  return pt?.cumulativeCloseRate ?? 0;
}

function daysSince(dateStr: string): number {
  return Math.max(0, Math.round((Date.now() - new Date(dateStr).getTime()) / 86_400_000));
}

const CURVE_METRIC_OPTIONS: Array<{ value: CurveMetric; label: string }> = [
  { value: "cumulativeCloseRate",  label: "Cumulative Close Rate" },
  { value: "cumulativeClosedWon",  label: "Cumulative Closed Deals" },
  { value: "cumulativeRevenue",    label: "Cumulative Cash Collected" },
  { value: "avgDaysToClose",       label: "Average Days to Close" },
];

const CURVE_SOURCE_OPTIONS = [
  { value: "all",       label: "All Sources" },
  { value: "youtube",   label: "YouTube" },
  { value: "instagram", label: "Instagram" },
  { value: "paid_meta", label: "Paid Meta" },
  { value: "organic",   label: "Organic" },
  { value: "referral",  label: "Referral" },
  { value: "email",     label: "Email" },
];

const CURVE_PAID_OPTIONS = [
  { value: "all",     label: "Paid + Organic" },
  { value: "paid",    label: "Paid Only" },
  { value: "organic", label: "Organic Only" },
];

function getCohortColor(index: number): string {
  const palette = [
    branding.colors.chart[1],
    branding.colors.chart[0],
    branding.colors.chart[2],
    branding.colors.chart[4],
    "var(--brand-accent)",
  ];
  return palette[index % palette.length];
}

function getCohortDash(index: number): string | undefined {
  return index >= 5 ? "6 3" : undefined;
}

interface CurveTooltipPayload {
  dataKey: string;
  color: string;
  value: number;
}

interface CurveTooltipProps {
  active?: boolean;
  payload?: CurveTooltipPayload[];
  label?: number;
  augmented: AugmentedCurveLine[];
  yFormatter: (v: number) => string;
  hoveredLine?: string | null;
}

function CurveTooltip({ active, payload, label, augmented, yFormatter, hoveredLine }: CurveTooltipProps) {
  if (!active || !payload?.length) return null;
  const day = label ?? 0;
  let entries = payload.filter(e => e.value != null && e.dataKey !== "day");
  if (hoveredLine) {
    const hoveredCohort = augmented.find(c => c.cohortWeek === hoveredLine);
    if (hoveredCohort) entries = entries.filter(e => e.dataKey === hoveredCohort.cohortLabel);
  }
  if (entries.length === 0) return null;

  return (
    <div className="bg-card border border-border rounded-xl p-3 text-xs shadow-xl max-w-[260px] space-y-2 z-50">
      <p className="font-semibold text-foreground border-b border-border pb-1.5">Day {day} since opt-in</p>
      {entries.map(entry => {
        const cohort = augmented.find(c => c.cohortLabel === entry.dataKey);
        if (!cohort) return null;
        const pt = [...cohort.points].reverse().find(p => p.day <= day);
        const i = augmented.indexOf(cohort);
        const color = getCohortColor(i);
        return (
          <div key={entry.dataKey} className="space-y-1">
            <p className="font-semibold" style={{ color }}>{entry.dataKey}</p>
            <div className="grid grid-cols-[1fr_auto] gap-x-4 text-muted-foreground leading-5">
              <span>Total optins</span><span className="text-right text-foreground font-medium">{cohort.totalLeads}</span>
              <span>Closed</span><span className="text-right text-foreground font-medium">{pt?.cumulativeClosedWon ?? 0}</span>
              <span>Close rate</span><span className="text-right text-foreground font-medium">{(pt?.cumulativeCloseRate ?? 0).toFixed(1)}%</span>
              <span>Cash collected</span><span className="text-right text-foreground font-medium">{fmtCur(pt?.cumulativeRevenue ?? 0)}</span>
              {(pt?.avgDaysToClose ?? 0) > 0 && (
                <><span>Avg days</span><span className="text-right text-foreground font-medium">{(pt?.avgDaysToClose ?? 0).toFixed(1)}d</span></>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CohortSalesCycleCurve({
  initialData,
  campaigns,
}: {
  initialData: CohortCurveLine[];
  campaigns: string[];
}) {
  const [yMetric, setYMetric]               = useState<CurveMetric>("cumulativeCloseRate");
  const [filters, setFilters]               = useState<CurveCampaignFilter>({ source: "all", paidOrganic: "all", campaign: "all" });
  const [curveData, setCurveData]           = useState<CohortCurveLine[]>(initialData);
  const [loadingCurve, setLoadingCurve]     = useState(false);
  const [visibleCohorts, setVisibleCohorts] = useState<string[]>([]);
  const [hoveredLine, setHoveredLine]       = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const hasF = filters.source !== "all" || filters.paidOrganic !== "all" || filters.campaign !== "all";
    if (!hasF) setCurveData(initialData);
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    abortRef.current?.abort();
    const hasF = filters.source !== "all" || filters.paidOrganic !== "all" || filters.campaign !== "all";
    if (!hasF) { setCurveData(initialData); return; }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoadingCurve(true);
    const params = new URLSearchParams();
    if (filters.source !== "all")      params.set("source",      filters.source);
    if (filters.paidOrganic !== "all") params.set("paidOrganic", filters.paidOrganic);
    if (filters.campaign !== "all")    params.set("campaign",    filters.campaign);
    fetch(`/api/buying-cycle/analytics?${params}`, { signal: ctrl.signal })
      .then(r => r.json() as Promise<{ analytics?: { cohortCurves: CohortCurveLine[] } }>)
      .then(data => { setCurveData(data.analytics?.cohortCurves ?? initialData); setLoadingCurve(false); })
      .catch(err => { if ((err as Error).name !== "AbortError") setLoadingCurve(false); });
    return () => ctrl.abort();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const augmented = useMemo(() => curveData.map(augmentCurve), [curveData]);

  // Keep visibleCohorts in sync when data changes (e.g. filter change)
  useEffect(() => {
    setVisibleCohorts(prev => {
      const valid = prev.filter(w => augmented.some(c => c.cohortWeek === w));
      return valid.length === prev.length ? prev : valid;
    });
  }, [augmented]);

  const visibleAugmented = useMemo(
    () => augmented.filter(c => visibleCohorts.includes(c.cohortWeek)),
    [augmented, visibleCohorts]
  );

  const yFormatter = (v: number) => {
    switch (yMetric) {
      case "cumulativeCloseRate": return fmtPct(v);
      case "cumulativeClosedWon": return fmtNum(v);
      case "cumulativeRevenue":   return fmtCur(v);
      case "avgDaysToClose":      return fmtDays(v);
    }
  };

  const chartData = useMemo(() => {
    const allDays = Array.from(new Set(visibleAugmented.flatMap(c => c.points.map(p => p.day)))).sort((a, b) => a - b);
    return allDays.map(day => {
      const row: Record<string, number> & { day: number } = { day };
      for (const cohort of visibleAugmented) {
        const maxDay = cohort.points[cohort.points.length - 1]?.day ?? 0;
        if (day > maxDay) continue;
        const pt = [...cohort.points].reverse().find(p => p.day <= day);
        if (pt) row[cohort.cohortLabel] = pt[yMetric as keyof AugmentedPoint] as number;
      }
      return row;
    });
  }, [visibleAugmented, yMetric]);

  const lastIndexPerCohort = useMemo(() => {
    const result: Record<string, number> = {};
    for (const cohort of visibleAugmented) {
      for (let idx = chartData.length - 1; idx >= 0; idx--) {
        if (chartData[idx][cohort.cohortLabel] !== undefined) { result[cohort.cohortLabel] = idx; break; }
      }
    }
    return result;
  }, [chartData, visibleAugmented]);

  const tableRows = useMemo(() => visibleAugmented.map((cohort) => {
    const i = augmented.indexOf(cohort);
    const ageDays = daysSince(cohort.cohortWeek);
    const finalPt = cohort.points[cohort.points.length - 1];
    const color   = getCohortColor(i);
    const rateStr = (threshold: number) => ageDays < threshold ? null : fmtPct(getRateAtDay(cohort, threshold));
    return {
      cohortWeek: cohort.cohortWeek,
      cohortLabel: cohort.cohortLabel,
      totalLeads: cohort.totalLeads,
      closed: finalPt?.cumulativeClosedWon ?? 0,
      d7Rate:  rateStr(7),
      d14Rate: rateStr(14),
      d30Rate: rateStr(30),
      avgDays: finalPt && finalPt.avgDaysToClose > 0 ? `${finalPt.avgDaysToClose.toFixed(1)}d` : "—",
      cash:    fmtCur(finalPt?.cumulativeRevenue ?? 0),
      ageDays, color,
    };
  }), [visibleAugmented, augmented]);

  const setCurveFilter = (key: keyof CurveCampaignFilter, val: string) =>
    setFilters(f => ({ ...f, [key]: val }));

  const campaignOptions = useMemo(() => [
    { value: "all", label: "All Campaigns" },
    ...campaigns.slice(0, 25).map(c => ({ value: c, label: c.length > 32 ? c.slice(0, 31) + "…" : c })),
  ], [campaigns]);

  if (augmented.length === 0 && !loadingCurve) {
    return (
      <AnalyticsCard title="Cohort Sales Cycle Curve" description="Cumulative close rate since opt-in, by weekly optin cohort">
        <p className="text-sm text-muted-foreground text-center py-8">No cohort data for selected filters.</p>
      </AnalyticsCard>
    );
  }

  const curveControls = (
    <div className="flex items-center gap-2 flex-wrap">
      <MiniSelect value={yMetric}             options={CURVE_METRIC_OPTIONS} onChange={v => setYMetric(v as CurveMetric)} />
      <MiniSelect value={filters.paidOrganic} options={CURVE_PAID_OPTIONS}   onChange={v => setCurveFilter("paidOrganic", v)} />
      <MiniSelect value={filters.source}      options={CURVE_SOURCE_OPTIONS} onChange={v => setCurveFilter("source", v)} />
      {campaigns.length > 0 && (
        <MiniSelect value={filters.campaign}  options={campaignOptions}      onChange={v => setCurveFilter("campaign", v)} />
      )}
      {loadingCurve && <span className="text-[10px] text-muted-foreground animate-pulse">Loading…</span>}
    </div>
  );

  return (
    <AnalyticsCard
      title="Cohort Sales Cycle Curve"
      description="Cumulative close rate since opt-in (optin week), by cohort."
      controls={curveControls}
    >
      <ResponsiveContainer width="100%" height={340}>
        <LineChart data={chartData} margin={{ top: 8, right: 120, bottom: 28, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis dataKey="day" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} tickFormatter={v => `D${v}`}
            label={{ value: "Days since opt-in", position: "insideBottom", offset: -12, fontSize: 10, fill: "var(--muted-foreground)" }} />
          <YAxis tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} tickFormatter={yFormatter} width={54} />
          <Tooltip content={<CurveTooltip augmented={visibleAugmented} yFormatter={yFormatter} hoveredLine={hoveredLine} />} />
          <ReferenceLine x={7}  stroke="var(--muted-foreground)" strokeDasharray="4 2" strokeOpacity={0.4}
            label={{ value: "D7",  position: "insideTopRight", fontSize: 9, fill: "var(--muted-foreground)" }} />
          <ReferenceLine x={14} stroke="var(--muted-foreground)" strokeDasharray="4 2" strokeOpacity={0.4}
            label={{ value: "D14", position: "insideTopRight", fontSize: 9, fill: "var(--muted-foreground)" }} />
          <ReferenceLine x={30} stroke="var(--muted-foreground)" strokeDasharray="4 2" strokeOpacity={0.4}
            label={{ value: "D30", position: "insideTopRight", fontSize: 9, fill: "var(--muted-foreground)" }} />
          {visibleAugmented.map((cohort) => {
            const globalI = augmented.indexOf(cohort);
            const color   = getCohortColor(globalI);
            const dash    = getCohortDash(globalI);
            const lastIdx = lastIndexPerCohort[cohort.cohortLabel] ?? -1;
            return (
              <Line key={cohort.cohortWeek} type="monotone" dataKey={cohort.cohortLabel}
                stroke={color} strokeWidth={2} strokeDasharray={dash} dot={(rawProps: unknown) => {
                  const p = rawProps as { cx?: number; cy?: number; index?: number; value?: number };
                  const isEnd = p.index === lastIdx;
                  if (!isEnd || !p.cx || !p.cy || p.value == null)
                    return <circle key={`dot-${cohort.cohortWeek}-${p.index}`} r={0} cx={0} cy={0} />;
                  const shortLabel = cohort.cohortLabel.replace("Week of ", "");
                  return (
                    <g key={`dot-${cohort.cohortWeek}-end`}>
                      <circle cx={p.cx} cy={p.cy} r={3.5} fill={color} />
                      <text x={p.cx + 7} y={p.cy + 4} fill={color} fontSize={9} fontWeight={500} style={{ pointerEvents: "none" }}>
                        {shortLabel}: {yFormatter(p.value)}
                      </text>
                    </g>
                  );
                }}
                activeDot={(props: unknown) => {
                  const p = props as { cx?: number; cy?: number };
                  return (
                    <circle cx={p.cx ?? 0} cy={p.cy ?? 0} r={5} fill={color} strokeWidth={0}
                      onMouseEnter={() => setHoveredLine(cohort.cohortWeek)}
                      onMouseLeave={() => setHoveredLine(null)}
                      style={{ cursor: "crosshair" }} />
                  );
                }}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>

      <div className="flex items-center gap-2 flex-wrap pt-1">
        {visibleAugmented.map((cohort) => {
          const globalI = augmented.indexOf(cohort);
          const color = getCohortColor(globalI);
          return (
            <div key={cohort.cohortWeek}
              className="flex items-center gap-1 text-[10px] px-2.5 py-0.5 rounded-full border font-semibold"
              style={{ color, borderColor: color, backgroundColor: `${color}18` }}>
              {cohort.cohortLabel.replace("Week of ", "")}
              {visibleCohorts.length > 1 && (
                <button
                  onClick={() => setVisibleCohorts(v => v.filter(w => w !== cohort.cohortWeek))}
                  className="opacity-60 hover:opacity-100 leading-none ml-0.5 text-[11px]">
                  ×
                </button>
              )}
            </div>
          );
        })}
        {augmented.filter(c => !visibleCohorts.includes(c.cohortWeek)).length > 0 && (
          <select
            className="text-[10px] px-2.5 py-0.5 rounded-full border border-dashed border-border text-muted-foreground bg-transparent cursor-pointer hover:border-muted-foreground/60 transition-colors"
            value=""
            onChange={e => { if (e.target.value) setVisibleCohorts(v => [...v, e.target.value]); }}>
            <option value="" disabled>+ Compare cohort</option>
            {augmented.filter(c => !visibleCohorts.includes(c.cohortWeek)).map(c => (
              <option key={c.cohortWeek} value={c.cohortWeek}>{c.cohortLabel.replace("Week of ", "")}</option>
            ))}
          </select>
        )}
      </div>

      <div className="pt-3 border-t border-border overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="text-left text-muted-foreground font-medium py-2 pr-4 whitespace-nowrap">Cohort</th>
              <th className="text-right text-muted-foreground font-medium py-2 px-2 whitespace-nowrap">Optins</th>
              <th className="text-right text-muted-foreground font-medium py-2 px-2 whitespace-nowrap">Closed</th>
              <th className="text-right text-muted-foreground font-medium py-2 px-2 whitespace-nowrap">Day 7 CR</th>
              <th className="text-right text-muted-foreground font-medium py-2 px-2 whitespace-nowrap">Day 14 CR</th>
              <th className="text-right text-muted-foreground font-medium py-2 px-2 whitespace-nowrap">Day 30 CR</th>
              <th className="text-right text-muted-foreground font-medium py-2 px-2 whitespace-nowrap">Avg Days</th>
              <th className="text-right text-muted-foreground font-medium py-2 pl-2 whitespace-nowrap">Cash</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map(row => (
                <tr key={row.cohortWeek}
                  className="border-t border-border/50 hover:bg-muted/10 transition-colors">
                  <td className="py-2 pr-4 font-semibold whitespace-nowrap" style={{ color: row.color }}>{row.cohortLabel}</td>
                  <td className="py-2 px-2 text-right text-muted-foreground">{row.totalLeads}</td>
                  <td className="py-2 px-2 text-right font-semibold text-foreground">{row.closed}</td>
                  <td className="py-2 px-2 text-right">{row.d7Rate  === null ? <span className="text-muted-foreground/40 italic text-[10px]">not enough data</span> : <span className="text-foreground">{row.d7Rate}</span>}</td>
                  <td className="py-2 px-2 text-right">{row.d14Rate === null ? <span className="text-muted-foreground/40 italic text-[10px]">not enough data</span> : <span className="text-foreground">{row.d14Rate}</span>}</td>
                  <td className="py-2 px-2 text-right">{row.d30Rate === null ? <span className="text-muted-foreground/40 italic text-[10px]">not enough data</span> : <span className="text-foreground">{row.d30Rate}</span>}</td>
                  <td className="py-2 px-2 text-right text-foreground">{row.avgDays}</td>
                  <td className="py-2 pl-2 text-right font-semibold text-[var(--brand-primary)]">{row.cash === "$0" ? "—" : row.cash}</td>
                </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AnalyticsCard>
  );
}

// ─────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────
export function LeadHistoryCohortSection({
  analytics,
}: {
  analytics: LeadHistoryAnalytics;
}) {
  const campaigns = analytics.audienceBreakdown.map(r => r.label).filter(Boolean);
  const [dqExpanded, setDqExpanded] = useState(false);

  return (
    <div className="space-y-8">
      <SectionDivider label="Optin Cohort Revenue Heatmap" />
      <CohortHeatmap data={analytics.cohortHeatmap} />

      <SectionDivider label="Audience Efficiency" />
      <AudienceEfficiencyAnalysis initialData={analytics.audienceBreakdown} campaigns={campaigns} />

      <SectionDivider label="Cohort Sales Cycle Curve" />
      <CohortSalesCycleCurve initialData={analytics.cohortCurves} campaigns={campaigns} />

      {analytics.meta.validationIssues.length > 0 && (
        <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 px-4 py-3">
          <button
            className="flex items-center gap-2 w-full text-left"
            onClick={() => setDqExpanded(v => !v)}
          >
            <span className="text-xs font-semibold text-yellow-400">
              {analytics.meta.validationIssues.length} data quality issue{analytics.meta.validationIssues.length !== 1 ? "s" : ""} detected
            </span>
            <span className="text-xs text-yellow-400/60 ml-auto">{dqExpanded ? "▲ hide" : "▼ show"}</span>
          </button>
          {dqExpanded && (
            <ul className="text-xs text-muted-foreground space-y-0.5 mt-2">
              {analytics.meta.validationIssues.map((issue, i) => (
                <li key={i}>{issue.leadId}: {issue.message}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
